import { adminOpenApiSchema, type AdminOpenApi } from '@steado/admin-contract/overview';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, HttpMethod, QueryValue } from '@/lib/api/client';

/**
 * The API console's two halves: read the document, then send a request against
 * the same API it describes.
 */

export function openApiQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'openapi'],
    queryFn: (): Promise<AdminOpenApi> => api.request({ path: '/admin/openapi', schema: adminOpenApiSchema }),
    // The document only changes when the API is redeployed.
    staleTime: 5 * 60_000,
  });
}

export interface ConsoleRequest {
  readonly method: HttpMethod;
  /** Already substituted and prefixed with the API version. */
  readonly path: string;
  readonly query: Readonly<Record<string, QueryValue>>;
  readonly body: unknown;
}

export interface ConsoleResponse {
  readonly durationMs: number;
  readonly data: unknown;
  readonly meta: Record<string, unknown>;
}

/**
 * Sends the request as the signed-in admin.
 *
 * Through the console's own client, so the request carries the operator's token
 * and nobody else's — design spec §11 removes the imported design's
 * "Impersonate user" toggle, and there is no server-side proxy that could mint
 * a different one. Whatever a request does here, the audit log will name the
 * person who pressed the button.
 *
 * The response is parsed as `unknown` on purpose: this is the one place in the
 * console that talks to endpoints the build has never heard of, so there is no
 * schema to hold it to beyond the envelope the client already checks.
 */
export async function sendConsoleRequest(api: ApiClient, request: ConsoleRequest): Promise<ConsoleResponse> {
  const startedAt = Date.now();

  const result = await api.requestWithMeta({
    path: request.path,
    method: request.method,
    query: request.query,
    schema: z.unknown(),
    ...(request.body === undefined ? {} : { body: request.body }),
  });

  return { durationMs: Date.now() - startedAt, data: result.data, meta: result.meta };
}
